import Moment from 'moment-timezone';

// Technically, all locales should work, but these are the only ones that we explicitly test for right now.
export enum Locale {
  EnUS = 'en-US',
  EnGB = 'en-GB',
}

// This is an enum to represent the various formats that we use throughout the app. The format in the comment is the
// equivalent Moment format string, assuming en-US locale.
export enum TimeFormat {
  DayOfMonth = 'DayOfMonth', // D
  LongDayOfWeekMonthDay = 'LongDayOfWeekMonthDay', // dddd, MMMM D
  LongDayOfWeekMonthDayAtTimeAndTimezone = 'LongDayOfWeekMonthDayAtTimeAndTimezone', // dddd, MMMM D [at] h:mm A z
  LongDayOfWeekMonthDayWithTimeAndComma = 'LongDayOfWeekMonthDayWithTimeAndComma', // dddd, MMMM D, h:mm A
  LongDayOfWeekMonthDayWithTimeAndTimezoneAndComma = 'LongDayOfWeekMonthDayWithTimeAndTimezoneAndComma', // dddd, MMMM D, h:mm A z
  LongDayOfWeekMonthDayYear = 'LongDayOfWeekMonthDayYear', // dddd, MMMM D, YYYY
  LongDayOfWeekMonthDayYearWithTimeAndComma = 'LongDayOfWeekMonthDayYearWithTimeAndComma', // dddd, MMMM D, YYYY, h:mm A
  LongDayOfWeekMonthDayYearWithTimeAndTimezoneAndComma = 'LongDayOfWeekMonthDayYearWithTimeAndTimezoneAndComma', // dddd, MMMM D, YYYY, h:mm A z
  LongMonthDay = 'LongMonthDay', // MMMM D
  LongMonthDayYear = 'LongMonthDayYear', // MMMM D, YYYY (LL)
  LongMonthDayYearWithTime = 'LongMonthDayYearWithTime', // MMMM D, YYYY h:mm A (LLL)
  LongMonthDayYearWithTimeAndTimezone = 'LongMonthDayYearWithTimeAndTimezone', // MMMM D, YYYY h:mm A z (LLL z)
  MonthDayYearWithSlashes = 'MonthDayYearWithSlashes', // M/D/YYYY (l)
  ShortDayOfWeek = 'ShortDayOfWeek', // ddd
  ShortDayOfWeekMonthDay = 'ShortDayOfWeekMonthDay', // ddd, MMM D
  ShortDayOfWeekMonthDayWithTimeAndTimezoneAndComma = 'ShortDayOfWeekMonthDayWithTimeAndTimezoneAndComma', // ddd, MMM D, h:mm A z
  ShortMonthDay = 'ShortMonthDay', // MMM D
  ShortMonthDayWithTimeAndComma = 'ShortMonthDayWithTimeAndComma', // MMM D, h:mm A
  ShortMonthDayWithTimeAndTimezoneAndComma = 'ShortMonthDayWithTimeAndTimezoneAndComma', // MMM D, h:mm A z
  ShortMonthDayYear = 'ShortMonthDayYear', // MMM D, YYYY (ll)
  ShortMonthDayYearWithTimeAndTimezone = 'ShortMonthDayYearWithTimeAndTimezone', // MMM D, YYYY h:mm A z (lll z)
  ShortMonthDayYearWithTimeAndTimezoneAndComma = 'ShortMonthDayYearWithTimeAndTimezoneAndComma', // MMM D, YYYY, h:mm A z (ll, LT z)
  Time = 'Time', // h:mm A (LT)
  TimeWithTimezone = 'TimeWithTimezone', // h:mm A z (LT z)
  TimeHourWithTimezone = 'TimeHourWithTimezone', // h A z
}

export function formatMoment (time: Moment.Moment, format: TimeFormat, locale?: string): string {
  locale = locale || window.navigator.language;

  switch (format) {
    case TimeFormat.DayOfMonth: {
      return time.locale(locale).format('D');
    }
    case TimeFormat.LongDayOfWeekMonthDay: {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
    }
    case TimeFormat.LongDayOfWeekMonthDayAtTimeAndTimezone: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date} at ${time.locale(locale).format('LT z')}`;
    }
    case TimeFormat.LongDayOfWeekMonthDayWithTimeAndComma: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT')}`;
    }
    case TimeFormat.LongDayOfWeekMonthDayWithTimeAndTimezoneAndComma: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT z')}`;
    }
    case TimeFormat.LongDayOfWeekMonthDayYear: {
      return new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: time.tz() }).format(time.toDate());
    }
    case TimeFormat.LongDayOfWeekMonthDayYearWithTimeAndComma: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT')}`;
    }
    case TimeFormat.LongDayOfWeekMonthDayYearWithTimeAndTimezoneAndComma: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT z')}`;
    }
    case TimeFormat.LongMonthDay: {
      return new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
    }
    case TimeFormat.LongMonthDayYear: {
      return time.locale(locale).format('LL');
    }
    case TimeFormat.LongMonthDayYearWithTime: {
      return time.locale(locale).format('LLL');
    }
    case TimeFormat.LongMonthDayYearWithTimeAndTimezone: {
      return time.locale(locale).format('LLL z');
    }
    case TimeFormat.MonthDayYearWithSlashes: {
      return time.locale(locale).format('l');
    }
    case TimeFormat.ShortDayOfWeek: {
      return time.locale(locale).format('ddd');
    }
    case TimeFormat.ShortDayOfWeekMonthDay: {
      return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
    }
    case TimeFormat.ShortDayOfWeekMonthDayWithTimeAndTimezoneAndComma: {
      const date = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT z')}`;
    }
    case TimeFormat.ShortMonthDay: {
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
    }
    case TimeFormat.ShortMonthDayWithTimeAndComma: {
      const date = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT')}`;
    }
    case TimeFormat.ShortMonthDayWithTimeAndTimezoneAndComma: {
      const date = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: time.tz() }).format(time.toDate());
      return `${date}, ${time.locale(locale).format('LT z')}`;
    }
    case TimeFormat.ShortMonthDayYear: {
      return time.locale(locale).format('ll');
    }
    case TimeFormat.ShortMonthDayYearWithTimeAndTimezone: {
      return time.locale(locale).format('lll z');
    }
    case TimeFormat.ShortMonthDayYearWithTimeAndTimezoneAndComma: {
      return time.locale(locale).format('ll, LT z');
    }
    case TimeFormat.Time: {
      return time.locale(locale).format('LT');
    }
    case TimeFormat.TimeWithTimezone: {
      return time.locale(locale).format('LT z');
    }
    case TimeFormat.TimeHourWithTimezone: {
      return time.locale(locale).format('h A z');
    }
  }
}

/*
 * Zones like America/Cancun, America/Regina, America/Phoenix, and
 * America/Puerto_Rico share an abbreviation with a major North American zone
 * family ("EST", "CST", "MST", "AST") while never observing daylight saving
 * time. While that family is on daylight saving, times in these zones read
 * like Eastern/Central/Mountain/Atlantic time but are an hour off, which can
 * make a candidate book the wrong hour without noticing.
 */
const DST_OBSERVING_COUNTERPARTS: Record<string, { name: string; timezone: string }> = {
  AST: { name: 'Atlantic Time', timezone: 'America/Halifax' },
  EST: { name: 'US Eastern Time', timezone: 'America/New_York' },
  CST: { name: 'US Central Time', timezone: 'America/Chicago' },
  MST: { name: 'US Mountain Time', timezone: 'America/Denver' },
};

export interface DstMismatch {
  abbreviation: string;
  counterpartName: string;
  counterpartTimezone: string;
  hoursBehind: number;
}

/*
 * Returns details when the given zone currently renders with a standard-time
 * abbreviation (e.g. "EST") while the zone family that abbreviation evokes is
 * on daylight saving time, i.e. displayed times are an hour off from what most
 * readers of that abbreviation expect. Returns null when there is no such
 * ambiguity at the reference time.
 */
export function getDstMismatch (timezone: string, reference: Moment.Moment = Moment()): DstMismatch | null {
  if (!Moment.tz.zone(timezone)) {
    return null;
  }

  const abbreviation = reference.clone().tz(timezone).format('z');
  const counterpart = DST_OBSERVING_COUNTERPARTS[abbreviation];
  if (!counterpart || counterpart.timezone === timezone) {
    return null;
  }

  const offset = reference.clone().tz(timezone).utcOffset();
  const counterpartOffset = reference.clone().tz(counterpart.timezone).utcOffset();
  // Zones elsewhere in the world can reuse an abbreviation (e.g. "CST" is also
  // China Standard Time), so only flag zones sitting exactly on the
  // counterpart's standard (winter) offset.
  const counterpartStandardOffset = Moment.tz({ year: reference.year(), month: 0, date: 15 }, counterpart.timezone).utcOffset();
  if (offset !== counterpartStandardOffset || offset === counterpartOffset) {
    return null;
  }

  return {
    abbreviation,
    counterpartName: counterpart.name,
    counterpartTimezone: counterpart.timezone,
    hoursBehind: (counterpartOffset - offset) / 60,
  };
}
